import { Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { DataService } from '../../../../services/data/data.service';
import { RouterService } from '../../../../services/router/router.service';
import { EnrollmentService } from '../../../../services/enrollment/enrollment.service';
import { APP_ROUTES } from '../../../../utils/route.constant';
import { IContractData, IPaymentInfo } from '../../../../interfaces/IJob';
import { ICrossSellContent } from '../../../../interfaces/ICrossSellContent';
import { IBaseCoverageResponse, IContractEnrollmentRequest } from '../../../../interfaces/IEnrollment';
import { getMonthlyCost, getTaxAmount, getTotalMonthlyCost } from '../special-member-offer-v2.helper';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LiferayService } from '../../../../services/liferay/liferay.service';
import { LIFERAY_WEB_CONSTANT } from '../../../../utils/liferay.constant';
import { IMessageByScenario } from '../../../../services/liferay/liferay.interface';
import { UtilitiesService } from '../../../../services/utilities/utilities.service';
import { AUTHORIZE_PAYMENT_FOR_CROSS_SELL_SCENARIO, CARD_TYPES, CONTACT_CONSENT_FOR_CROSS_SELL_SCENARIO, PAYMENTECH, PAYMENT_TYPE_BANK_ACCOUNT, PAYMENT_TYPE_CREDIT_CARD } from '../../../../utils/constants';
import { SPECIAL_MEMBER_OFFER_CONTENT } from '../special-member-offer-v2.config';
import { Ga4Service } from '../../../../services/ga4/ga4.service';
import { ALL_PAGES, CE_BACK_CONTACT_DETAILS_PAGE, CE_CONTINUE_CONTACT_DETAILS_PAGE, CE_ENROLLMENT_ERROR, CE_ENROLLMENT_IS_DONE, ERR_EVENT } from '../../../../services/ga4/ga4.constant';
import { IPaymentType } from '../../../../interfaces/IPayments';
import { PaymentService } from '../../../../services/payment/payment.service';
declare const ka: any;
@Component({
  selector: 'app-enroll-review',
  templateUrl: './enroll-review.component.html',
  styleUrls: ['./enroll-review.component.scss']
})
export class EnrollReviewComponent implements OnInit, OnDestroy {
  cleaner$ = new Subject<void>();
  contractData: IContractData | null = null;
  currentPaymentInfo: IPaymentInfo | null = null;
  contentToBeDisplayed: ICrossSellContent | null = null;
  productDetails: IBaseCoverageResponse | null = null;
  isSubmitting = false;
  enrollmentSuccess = false;
  enrollmentError: string | null = null;
  sampleContractUrl: string | null = null;
  monthlyCost: number | null = null;
  taxAmount: number | null = null;
  totalMonthlyCost: number | null = null;
  paymentPreference: string | null = null;
  paymentMethodDisplay: string = '';
  termsForm!: FormGroup;
  type: string | null = null;
  paymentTermsVerbiage: string = '';
  content = SPECIAL_MEMBER_OFFER_CONTENT;
  // payment forms states variables
  addNewCard = false;
  addNewBankAccount = false;
  newCardAdded = false;
  newBankAccountAdded = false;
  isPaymentSaved = false;
  isPaymentRequiredError = false;
  paymentTypeSelected: IPaymentType | null = null;
  paymentTypeCreditCard: IPaymentType = PAYMENT_TYPE_CREDIT_CARD;
  paymentTypeBankAccount: IPaymentType = PAYMENT_TYPE_BANK_ACCOUNT;

  constructor(
    private data: DataService,
    private routerService: RouterService,
    private enrollService: EnrollmentService,
    private liferayService: LiferayService,
    private util: UtilitiesService,
    private gaService: Ga4Service,
    public paymentService: PaymentService
  ) { }

  ngOnInit(): void {
    this.type = this.routerService.getQueryParam('type');
    this.termsForm = new FormGroup({
      agreeToTerms: new FormControl(false, Validators.requiredTrue)
    });

    combineLatest({
      contractData: this.data.contractData$,
      sampleContractPdfUrl: this.data.sampleContractPdfUrl$,
      productInfo: this.data.productInfo$,
      crossSellContent: this.data.crossSellContent$,
      enrollmentPaymentInfo: this.data.enrollmentPaymentInfo$
    }).pipe(takeUntil(this.cleaner$))
      .subscribe({
        next: ({ contractData, sampleContractPdfUrl, productInfo, crossSellContent, enrollmentPaymentInfo }) => {
          if (!contractData || !productInfo) {
            setTimeout(() => {
              if (!this.data.contractData$.value || !this.data.productInfo$.value) {
                this.goBack();
              }
            }, 0);
            return;
          }
          this.contractData = contractData;
          this.currentPaymentInfo = enrollmentPaymentInfo || null;
          this.paymentPreference = this.currentPaymentInfo?.billMechCode || null;
          this.initSavedPaymentForms();
          this.getTermsVerbiageFromLiferay();
          this.sampleContractUrl = sampleContractPdfUrl;
          this.productDetails = productInfo;
          this.monthlyCost = getMonthlyCost(this.productDetails?.productInfo?.cost) ?? null;
          this.taxAmount = getTaxAmount(this.productDetails?.taxRate, this.monthlyCost) ?? null;
          this.totalMonthlyCost = getTotalMonthlyCost(this.monthlyCost, this.taxAmount) ?? null;
          this.contentToBeDisplayed = crossSellContent;
        }
      });
  }

  initSavedPaymentForms(): void {
    if (this.currentPaymentInfo) {
      const p = this.currentPaymentInfo;
      if (p.billMechCode === PAYMENT_TYPE_CREDIT_CARD) {
        this.paymentService.ccForm.patchValue({
          firstName: p.creditCardFirstName || '',
          lastName: p.creditCardLastName || '',
          cardNumber: p.creditCardNumber || '',
          expirationMonth: p.creditCardExpMonth ? String(p.creditCardExpMonth).padStart(2, '0') : '',
          expirationYear: p.creditCardExpYear ? String(p.creditCardExpYear) : '',
          typeCode: p.creditCardTypeCode || ''
        });
      } else if (p.billMechCode === PAYMENT_TYPE_BANK_ACCOUNT) {
        this.paymentService.bankAccForm.patchValue({
          accountNumber: p.bankAcctNumber || '',
          routingNumber: p.bankAbaNumber || '',
          accountTypes: p.bankAcctType || ''
        });
      }
    }
  }

  getTermsVerbiageFromLiferay(): void {
    this.liferayService.getWebContent<{ messageByScenario: IMessageByScenario[] }>(
      LIFERAY_WEB_CONSTANT.paymentTermsAndConditions.type,
      LIFERAY_WEB_CONSTANT.paymentTermsAndConditions.id
    )
      .pipe(takeUntil(this.cleaner$))
      .subscribe({
        next: (data) => {
          if (!data) return;
          const messagesByScenario = data.value?.messageByScenario ?? [];
          this.paymentTermsVerbiage = this.util.setHtmlContent(messagesByScenario.find((term) => term.scenario === AUTHORIZE_PAYMENT_FOR_CROSS_SELL_SCENARIO)?.message ?? '')
            .replace("{{paymentPreference}}", this.paymentPreference === PAYMENTECH ? 'credit card' : 'bank account')
            .replace("{{contact}}", this.contentToBeDisplayed?.contact || '');
        }
      });
  }

  openSampleContract(): void {
    if (this.sampleContractUrl) {
      window.open(this.sampleContractUrl, '_blank');
    }
  }

  confirmEnrollment(): void {
    if (this.isSubmitting) {
      return;
    }

    if (this.type === 'CE') {
      this.gaService.setGa4DtcSearsLanding(CE_CONTINUE_CONTACT_DETAILS_PAGE, this.contractData?.baseContract?.clientId ?? 'Unknown Client');
    }

    if (!this.termsForm.valid) {
      this.termsForm.markAllAsTouched();
    }

    if (!this.hasSavedPaymentInfo && !this.addNewCard && !this.addNewBankAccount) {
      this.isPaymentRequiredError = true;
    }

    const isInlinePaymentInvalid = (this.addNewCard || this.addNewBankAccount) && this.isPaymentFormInvalid();
    if (isInlinePaymentInvalid) {
      this.markActivePaymentFormTouched();
    }

    if (!this.termsForm.valid || (!this.hasSavedPaymentInfo && !this.paymentPreference) || isInlinePaymentInvalid) {
      return;
    }

    if (!this.contractData?.baseContract?.contractNumber || !this.contentToBeDisplayed?.cellCode || !this.paymentPreference) {
      this.gaService.setGa4DtcSearsLanding(CE_ENROLLMENT_ERROR, this.contractData?.baseContract?.clientId ?? 'Unknown Client', true);
      this.enrollmentError = `Oops, something went wrong. please call us at ${this.contentToBeDisplayed?.contact || ''} to complete your enrollment.`;
      return;
    }

    this.isSubmitting = true;
    this.enrollmentError = null;
    this.data.enrollmentInfo$.next(null);

    const payload = this.buildEnrollmentPayload();

    this.enrollService.enrollContract(payload)
      .pipe(takeUntil(this.cleaner$))
      .subscribe({
        next: (response) => {
          this.isSubmitting = false;
          if (response.successFlag === 'Y') {
            this.data.enrollmentInfo$.next(response);
            this.paymentService.ccForm.reset();
            this.gaService.setGa4DtcSearsLanding(CE_ENROLLMENT_IS_DONE, this.contractData?.baseContract?.clientId ?? 'Unknown Client', true);
            this.type
              ? this.routerService.navigateWithQuery(APP_ROUTES.SPECIAL_MEMBER_OFFER_V2_ENROLL_SUCCESS, { type: this.type })
              : this.routerService.goto(APP_ROUTES.SPECIAL_MEMBER_OFFER_V2_ENROLL_SUCCESS);
          } else {
            this.gaService.setGa4DtcSearsLanding(CE_ENROLLMENT_ERROR, this.contractData?.baseContract?.clientId ?? 'Unknown Client', true);
            this.enrollmentError = response.errorMessage || response.message || `An error occurred during enrollment. Please call us at ${this.contentToBeDisplayed?.contact || ''} to complete your enrollment.`;
          }
        },
        error: (err) => {
          this.isSubmitting = false;
          this.gaService.setGa4DtcSearsLanding(CE_ENROLLMENT_ERROR, this.contractData?.baseContract?.clientId ?? 'Unknown Client', true);
          this.enrollmentError = err?.message || `An error occurred during enrollment. Please call us at ${this.contentToBeDisplayed?.contact || ''} to complete your enrollment.`;
        }
      });
  }

  private buildEnrollmentPayload(): IContractEnrollmentRequest {
    const isCreditCard = this.paymentPreference === PAYMENTECH;
    const cc = this.paymentService.ccForm.value;
    const bank = this.paymentService.bankAccForm.value;
    const pInfo = this.currentPaymentInfo;

    return {
      contractNumber: this.contractData?.baseContract?.contractNumber || '',
      cellCode: this.contentToBeDisplayed?.cellCode || '',
      billMechCode: this.paymentPreference || '',
      creditCardFirstName: isCreditCard ? (pInfo?.creditCardFirstName || cc.firstName || '') : '',
      creditCardLastName: isCreditCard ? (pInfo?.creditCardLastName || cc.lastName || '') : '',
      creditCardTypeCode: isCreditCard ? ((pInfo?.creditCardTypeCode || cc.typeCode || '')?.toUpperCase()) : '',
      creditCardNumber: isCreditCard ? (pInfo?.creditCardNumber || cc.cardNumber || '') : '',
      creditCardExpMonth: isCreditCard ? ((pInfo?.creditCardExpMonth ? String(pInfo.creditCardExpMonth).padStart(2, '0') : '') || cc.expirationMonth || '') : '',
      creditCardExpYear: isCreditCard ? ((pInfo?.creditCardExpYear ? String(pInfo.creditCardExpYear) : '') || cc.expirationYear || '') : '',
      creditCardCVV: isCreditCard ? (cc.cvv || '') : '',
      bankAbaNumber: !isCreditCard ? (pInfo?.bankAbaNumber || bank.routingNumber || '') : '',
      bankAcctNumber: !isCreditCard ? (pInfo?.bankAcctNumber || bank.accountNumber || '') : '',
      bankAcctType: !isCreditCard ? (pInfo?.bankAcctType || bank.accountTypes || '') : '',
      safetechSessionId: (typeof ka !== 'undefined' && ka?.sessionId) ? ka.sessionId : null,
    };
  }

  goBack(): void {
    if (this.type === 'CE') {
      this.gaService.setGa4DtcSearsLanding(CE_BACK_CONTACT_DETAILS_PAGE, this.contractData?.baseContract?.clientId ?? 'Unknown Client');
    }
    this.type ? this.routerService.navigateWithQuery(APP_ROUTES.SPECIAL_MEMBER_OFFER_V2, { type: this.type }) : this.routerService.goto(APP_ROUTES.DASHBOARD);
  }

  // add card or bank account related methods
  addOrEditCard = () => {
    this.addNewCard = true;
    this.addNewBankAccount = false;
    this.isPaymentRequiredError = false;
    this.paymentTypeSelected = PAYMENT_TYPE_CREDIT_CARD;
    this.paymentPreference = PAYMENT_TYPE_CREDIT_CARD;
    if (this.currentPaymentInfo && this.currentPaymentInfo.billMechCode === PAYMENT_TYPE_CREDIT_CARD) {
      const p = this.currentPaymentInfo;
      if (p.creditCardFirstName || p.creditCardNumber) {
        this.paymentService.ccForm.patchValue({
          firstName: p.creditCardFirstName || '',
          lastName: p.creditCardLastName || '',
          cardNumber: p.creditCardNumber || '',
          expirationMonth: p.creditCardExpMonth ? String(p.creditCardExpMonth).padStart(2, '0') : '',
          expirationYear: p.creditCardExpYear ? String(p.creditCardExpYear) : '',
          typeCode: p.creditCardTypeCode || ''
        });
      }
    } else {
      this.paymentService.ccForm.reset();
    }
    this.getTermsVerbiageFromLiferay();
  };

  addOrEditBankAccount = () => {
    this.addNewBankAccount = true;
    this.addNewCard = false;
    this.isPaymentRequiredError = false;
    this.paymentTypeSelected = PAYMENT_TYPE_BANK_ACCOUNT;
    this.paymentPreference = PAYMENT_TYPE_BANK_ACCOUNT;
    if (this.currentPaymentInfo && this.currentPaymentInfo.billMechCode === PAYMENT_TYPE_BANK_ACCOUNT) {
      const p = this.currentPaymentInfo;
      if (p.bankAcctNumber) {
        this.paymentService.bankAccForm.patchValue({
          accountNumber: p.bankAcctNumber || '',
          routingNumber: p.bankAbaNumber || '',
          accountTypes: p.bankAcctType || ''
        });
      }
    } else {
      this.paymentService.bankAccForm.reset();
    }
    this.getTermsVerbiageFromLiferay();
  };

  editSavedPayment = () => {
    if (this.paymentPreference === PAYMENT_TYPE_BANK_ACCOUNT || this.currentPaymentInfo?.billMechCode === PAYMENT_TYPE_BANK_ACCOUNT) {
      this.addOrEditBankAccount();
    } else {
      this.addOrEditCard();
    }
  };

  get hasSavedPaymentInfo(): boolean {
    if (this.isPaymentSaved) {
      return true;
    }
    const info = this.currentPaymentInfo;
    return !!(info && (info.creditCardNumber || info.bankAcctNumber));
  }

  get hasSavedCreditCard(): boolean {
    if (!this.hasSavedPaymentInfo) return false;
    const code = this.currentPaymentInfo?.billMechCode || this.paymentPreference;
    return code === PAYMENT_TYPE_CREDIT_CARD;
  }

  get hasSavedBankAccount(): boolean {
    if (!this.hasSavedPaymentInfo) return false;
    const code = this.currentPaymentInfo?.billMechCode || this.paymentPreference;
    return code === PAYMENT_TYPE_BANK_ACCOUNT;
  }

  getSavedCardLastFour(): string {
    const fromSaved = this.currentPaymentInfo?.creditCardNumber;
    const fromForm = this.paymentService.ccForm.value.cardNumber;
    return this.getLastFourDigits(fromSaved || fromForm || '');
  }

  getSavedBankAccountLastFour(): string {
    const fromSaved = this.currentPaymentInfo?.bankAcctNumber;
    const fromForm = this.paymentService.bankAccForm.value.accountNumber;
    return this.getLastFourDigits(fromSaved || fromForm || '');
  }

  getCardTypeImage(typeCode?: string | null): string {
    const code = typeCode ?? this.currentPaymentInfo?.creditCardTypeCode;
    const key = code ? code.toLowerCase() : 'unknown';
    return (CARD_TYPES[key] || CARD_TYPES['unknown']).src;
  }


  getLastFourDigits(value: string) {
    if (!value) return '';
    return value.slice(-4);
  }

  cancelAddingPayment = () => {
    this.addNewCard = false;
    this.addNewBankAccount = false;
    this.paymentTypeSelected = null;
    this.paymentPreference = this.currentPaymentInfo?.billMechCode || null;
    this.isPaymentRequiredError = !this.hasSavedPaymentInfo;
    this.paymentService.ccForm.reset();
    this.paymentService.bankAccForm.reset();
    this.initSavedPaymentForms();
    this.getTermsVerbiageFromLiferay();
  };

  isPaymentFormInvalid(): boolean {
    if (this.addNewCard) {
      return this.paymentService.ccForm.invalid || this.paymentService.billingInfoForm.invalid;
    }
    if (this.addNewBankAccount) {
      return this.paymentService.bankAccForm.invalid || this.paymentService.billingInfoForm.invalid;
    }
    return false;
  }

  private markActivePaymentFormTouched(): void {
    if (this.addNewCard) {
      this.paymentService.ccForm.markAllAsTouched();
    } else if (this.addNewBankAccount) {
      this.paymentService.bankAccForm.markAllAsTouched();
    }
    this.paymentService.billingInfoForm.markAllAsTouched();
  }

  private buildCardPaymentInfo(): IPaymentInfo {
    return {
      ...(this.currentPaymentInfo || {}),
      contractNumber: this.contractData?.baseContract?.contractNumber || '',
      billMechCode: PAYMENT_TYPE_CREDIT_CARD,
      creditCardFirstName: this.paymentService.ccForm.value.firstName || '',
      creditCardLastName: this.paymentService.ccForm.value.lastName || '',
      creditCardTypeCode: (this.paymentService.ccForm.value.typeCode)?.toUpperCase() || '',
      creditCardNumber: this.paymentService.ccForm.value.cardNumber || '',
      creditCardExpMonth: Number(this.paymentService.ccForm.value.expirationMonth) || 0,
      creditCardExpYear: Number(this.paymentService.ccForm.value.expirationYear) || 0,
      bankAbaNumber: '',
      bankAcctNumber: '',
      bankAcctType: '',
      safetechSessionId: null,
      paymentProfileId: null
    };
  }

  private buildBankAccountPaymentInfo(): IPaymentInfo {
    return {
      ...(this.currentPaymentInfo || {}),
      contractNumber: this.contractData?.baseContract?.contractNumber || '',
      billMechCode: PAYMENT_TYPE_BANK_ACCOUNT,
      creditCardFirstName: '',
      creditCardLastName: '',
      creditCardTypeCode: '',
      creditCardNumber: null,
      creditCardExpMonth: 0,
      creditCardExpYear: 0,
      bankAbaNumber: this.paymentService.bankAccForm.value.routingNumber || '',
      bankAcctNumber: this.paymentService.bankAccForm.value.accountNumber || '',
      bankAcctType: this.paymentService.bankAccForm.value.accountTypes || '',
      safetechSessionId: null,
      paymentProfileId: null
    };
  }

  savePaymentInformation(): void {
    if (this.isPaymentFormInvalid()) {
      this.markActivePaymentFormTouched();
      return;
    }

    const updatedPaymentInfo = this.addNewCard
      ? this.buildCardPaymentInfo()
      : this.buildBankAccountPaymentInfo();

    this.currentPaymentInfo = updatedPaymentInfo;
    this.data.enrollmentPaymentInfo$.next(updatedPaymentInfo);

    this.newCardAdded = this.addNewCard;
    this.newBankAccountAdded = this.addNewBankAccount;
    this.isPaymentSaved = true;
    this.isPaymentRequiredError = false;
    this.addNewCard = false;
    this.addNewBankAccount = false;
    this.paymentPreference = this.paymentTypeSelected;
    this.getTermsVerbiageFromLiferay();
  }

  ngOnDestroy(): void {
    this.cleaner$.next();
    this.cleaner$.complete();
  }
}

